import { EntityServiceAPI } from 'api/EntityServiceAPI';

/** Why a check could not produce a result. The provider is never named. */
export type VerificationCheckErrorCategory =
  | 'not_configured'
  | 'bad_response'
  | 'not_saved'
  | 'unexpected'
  | 'provider_unavailable'
  | 'rate_limited';

/** A Check Error, stored beside the check's result and cleared by its next success. */
export interface VerificationCheckError {
  category: VerificationCheckErrorCategory;
  occurredAt: string;
}

interface BusinessVerificationRecord {
  association_id?: string | number;
  database_entity_id?: string | number;
  database_id?: string | number;
}

export type VerificationRunCheckStatus =
  | 'completed'
  | 'skipped'
  | 'failed'
  | 'unavailable';

/** One Verification Check's outcome in a run, as the entity service reports it. */
export interface VerificationRunCheck {
  verificationType: string;
  status: VerificationRunCheckStatus;
  reason?: string;
  /** Set when the check did not complete because of a Check Error. */
  errorCategory?: VerificationCheckErrorCategory;
}

/** Why the service would not run, mapped from its status and `code`. */
export type VerificationRunRefusal =
  | 'addon-required'
  | 'database-off'
  | 'nothing-to-verify'
  | 'in-progress'
  | 'no-credits'
  | 'not-found'
  | 'membership-unavailable'
  | 'unreachable';

export type VerificationRunOutcome =
  | { ok: true; checks: VerificationRunCheck[] }
  | { ok: false; refusal: VerificationRunRefusal; message: string };

type VerificationRunResponse =
  | {
      success: boolean;
      status: number;
      code?: string;
      result: any;
    }
  | undefined;

interface VerificationRunApi {
  runBusinessVerification: (payload: {
    databaseId: string;
    databaseEntityId: string;
  }) => Promise<VerificationRunResponse>;
}

const optionalText = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined;
  const normalized = String(value).trim();
  return normalized || undefined;
};

const REFUSALS_BY_CODE: Record<string, VerificationRunRefusal> = {
  ENTITY_ADDON_REQUIRED: 'addon-required',
  DATABASE_VERIFICATION_OFF: 'database-off',
  NOTHING_TO_VERIFY: 'nothing-to-verify',
  RUN_IN_PROGRESS: 'in-progress',
  // The run's credit gate (403) and its per-check reservation (402).
  VERIFICATION_CREDITS_EXHAUSTED: 'no-credits',
  NO_VERIFICATION_CREDITS: 'no-credits',
  MEMBERSHIP_UNAVAILABLE: 'membership-unavailable',
};

/**
 * Starts a Verification Run for one Database Entity. The entity service reads
 * the record's details itself, runs every check they allow (except adverse
 * media), saves each result to the record and answers once all are done.
 */
export const runBusinessVerification = async (
  record: BusinessVerificationRecord,
  api: VerificationRunApi = EntityServiceAPI,
): Promise<VerificationRunOutcome> => {
  const databaseEntityId = optionalText(
    record.association_id ?? record.database_entity_id,
  );
  const databaseId = optionalText(record.database_id);
  if (!databaseEntityId || !databaseId) {
    return {
      ok: false,
      refusal: 'not-found',
      message: 'This record is not in a database, so it cannot be verified.',
    };
  }

  // The client turns HTTP failures into a response; anything it still throws
  // (it could not even build the request) is the service being unreachable.
  let response: VerificationRunResponse;
  try {
    response = await api.runBusinessVerification({
      databaseId: databaseId,
      databaseEntityId: databaseEntityId,
    });
  } catch {
    response = undefined;
  }
  if (response?.success) {
    return {
      ok: true,
      checks: Array.isArray(response.result?.checks)
        ? response.result.checks
        : [],
    };
  }

  const message =
    typeof response?.result === 'string' && response.result
      ? response.result
      : 'Verification could not start. Check your connection and try again.';
  const byCode = response?.code ? REFUSALS_BY_CODE[response.code] : undefined;
  if (byCode) return { ok: false, refusal: byCode, message: message };
  if (response?.status === 404) {
    return { ok: false, refusal: 'not-found', message: message };
  }
  // Timeouts and network errors carry axios wording; say what happened instead.
  return {
    ok: false,
    refusal: 'unreachable',
    message:
      'The verification service did not respond, so nothing was updated. Try again in a moment.',
  };
};
